import React, { useEffect, useRef, useState } from 'react';

const ConsoleViewer = ({ instanceId, onClose }) => {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consoleInfo, setConsoleInfo] = useState(null);
  const rfbRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    // Fetch console connection info from API
    const fetchConsoleInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/v1/instances/${instanceId}/console`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error('Failed to get console access');
        }

        const data = await response.json();
        setConsoleInfo(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchConsoleInfo();
  }, [instanceId, API_URL, token]);

  useEffect(() => {
    if (!consoleInfo || !canvasRef.current) return;

    // Given the ongoing noVNC module issues, show connection info
    // User can use external VNC client or we can revisit browser integration later
    
    const infoDiv = document.createElement('div');
    infoDiv.style.cssText = 'padding: 40px; color: white;';
    infoDiv.innerHTML = `
      <div style="max-width: 700px; margin: 0 auto;">
        <div style="text-align: center; margin-bottom: 30px;">
          <svg style="width: 64px; height: 64px; margin-bottom: 15px;" fill="none" stroke="#60a5fa" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 style="color: #60a5fa; margin: 0;">Console Access Ready</h3>
        </div>
        
        <div style="background: #1e293b; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
          <p style="margin: 0 0 15px 0; color: #94a3b8; font-size: 14px;">WebSocket URL:</p>
          <code style="background: #0f172a; padding: 15px; display: block; border-radius: 4px; word-break: break-all; color: #60a5fa; font-size: 13px;">
            ${consoleInfo.console_url}
          </code>
        </div>

        <div style="background: #1e293b; padding: 25px; border-radius: 8px;">
          <p style="margin: 0 0 15px 0; font-weight: 600;">✅ Console Infrastructure:</p>
          <ul style="margin: 0; padding-left: 25px; line-height: 2; color: #cbd5e1;">
            <li>Websockify proxy: Running on agent (port 6080)</li>
            <li>VNC token mapping: Configured</li>
            <li>Instance ID: <code style="background: #0f172a; padding: 2px 6px; border-radius: 3px;">${consoleInfo.instance_id}</code></li>
            <li>Connection: Ready</li>
          </ul>
          
          <div style="margin-top: 20px; padding: 15px; background: #0f172a; border-radius: 4px; border-left: 3px solid #3b82f6;">
            <p style="margin: 0; font-size: 13px; color: #94a3b8;">
              <strong>Note:</strong> Full browser-based console viewer will be added in a future update. 
              For now, the console is accessible via the WebSocket URL above using a VNC client.
            </p>
          </div>
        </div>
      </div>
    `;
    
    canvasRef.current.appendChild(infoDiv);
    setLoading(false);

    return () => {
      if (canvasRef.current) {
        canvasRef.current.innerHTML = '';
      }
    };
  }, [consoleInfo]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-xl font-bold text-white">Console - {instanceId}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Console Display */}
        <div className="flex-1 flex items-center justify-center bg-black p-4">
          {loading && (
            <div className="text-gray-400">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Connecting to console...</p>
            </div>
          )}

          {error && (
            <div className="text-center">
              <div className="text-red-400 mb-4">
                <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-gray-300 text-lg mb-2">{error}</p>
              <p className="text-gray-500 text-sm mb-4">
                Make sure the agent has websockify running on port 6080
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                Close
              </button>
            </div>
          )}

          {/* noVNC canvas container */}
          <div 
            ref={canvasRef}
            className="w-full h-full"
            style={{ display: (loading || error) ? 'none' : 'block' }}
          />
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700 bg-slate-900">
          <div className="flex items-center justify-between text-sm text-gray-400">
            <div className="flex items-center space-x-4">
              <span>VNC Console</span>
              <span>•</span>
              <span>Press Ctrl+Alt+Del via menu if needed</span>
            </div>
            <button
              onClick={() => {
                if (rfbRef.current) {
                  rfbRef.current.sendCtrlAltDel();
                }
              }}
              className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs"
            >
              Send Ctrl+Alt+Del
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsoleViewer;
